import { Dialog } from '@headlessui/react';
import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Button } from '../../../ui';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  isDeleting: boolean;
  confirmButtonLabel?: string;
  processingLabel?: string;
  titleIcon?: IconDefinition;
  confirmButtonIcon?: IconDefinition;
  /** 'danger' (default) for destructive confirms; 'accent' for positive ones. */
  tone?: 'danger' | 'accent';
  /** Stacking order. Default 50 sits above page chrome and layer-0 SidePanels
      (z 40/41); pass 100 when confirming from inside a layered panel. */
  zIndex?: number;
  /** Extra content between the message and the buttons (e.g. an optional note field). */
  children?: React.ReactNode;
}

export default function DeleteConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Delete Task',
  message = 'Are you sure you want to delete this task? This action cannot be undone.',
  isDeleting = false,
  confirmButtonLabel = 'Delete',
  processingLabel,
  titleIcon = faTrash,
  confirmButtonIcon,
  tone = 'danger',
  zIndex = 50,
  children,
}: DeleteConfirmationModalProps) {
  const resolvedConfirmIcon = confirmButtonIcon || titleIcon || faTrash;
  const defaultProcessingLabel = (() => {
    const base = confirmButtonLabel.trim();
    if (!base) return 'Processing...';
    return base.endsWith('e') ? `${base.slice(0, -1)}ing...` : `${base}ing...`;
  })();
  const resolvedProcessingLabel = processingLabel || defaultProcessingLabel;

  // Add keyboard event listener for Enter key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Enter in a multi-line field is a newline, not a confirm.
      if (event.target instanceof HTMLTextAreaElement) return;
      if (event.key === 'Enter' && isOpen && !isDeleting) {
        event.preventDefault();
        onConfirm();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, isDeleting, onConfirm]);

  return (
    <Dialog open={isOpen} onClose={() => !isDeleting && onClose()} className="relative" style={{ zIndex }}>
      <div className="fixed inset-0" style={{ background: 'var(--surface-overlay)' }} aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4 ck-safe-pad">
        <Dialog.Panel
          className="mx-auto max-w-md w-full"
          style={{
            background: 'var(--surface-card)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-pop)',
            padding: 'var(--space-6)',
          }}
        >
          <Dialog.Title
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '11px',
              margin: '0 0 12px',
              fontSize: 'var(--text-md)',
              fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              letterSpacing: 'var(--tracking-tight)',
              color: 'var(--text-body)',
            }}
          >
            <FontAwesomeIcon
              icon={titleIcon || faTrash}
              style={{ color: tone === 'accent' ? 'var(--accent-text)' : 'var(--danger-400)', fontSize: '15px' }}
            />
            {title}
          </Dialog.Title>

          <p style={{ margin: '0 0 22px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {message}
          </p>

          {children ? <div style={{ margin: '-6px 0 20px' }}>{children}</div> : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button variant="ghost" onClick={onClose} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant={tone === 'accent' ? 'primary' : 'danger'} onClick={onConfirm} disabled={isDeleting}>
              <FontAwesomeIcon
                icon={isDeleting ? faSpinner : resolvedConfirmIcon}
                className={isDeleting ? 'animate-spin' : undefined}
                style={{ fontSize: '12px', marginRight: '7px' }}
              />
              {isDeleting ? resolvedProcessingLabel : confirmButtonLabel}
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
